import * as React from "react";
import { Link, useParams } from "react-router-dom";
import * as Moment from "moment";
import { DataItemImportance, Permission } from "../misc/api/apiTypes";
import { useCurrentStudyConfigurationContext } from "../Core/CurrentStudyConfigurationContext";
import LoadingWithMessage from "../misc/components/LoadingWithMessage";
import { isoToHHmmss } from "../misc/timeFormatting";
import { NotesModal } from "../misc/components/NotesModal";
import { RevealPinModal } from "./RevealPinModal";
import Condition from "../misc/components/Condition";
import { Authorization } from "../misc/components/Authorization";
import { PermissionEnum } from "../misc/api/ApiPermissionEnum";
import { MainState } from "../Core/reducers";
import { connect } from "react-redux";
import { SessionTypes } from "../misc/api/ApiSessionTypes";
import { ToggleRandomizationModal } from "./ToggleRandomizationModal";
import { Subject, SubjectNote, WebPortalClient, SubjectMutation } from "webportalclient";
import { useReferenceContextProvider } from "../Core/ReferenceContextProvider";
import { ConfigurableDataItemIds } from "../misc/api/ApiConfigurableDataItems";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../misc/api/WebPortalTokenClient";
import { useSecurityContext } from "../Core/SecurityContext";
import { AuditModal } from '../auditmodal/components/AuditModal';
import { TerminationDateModal } from "./TerminationDateModal";
import Alert, { AlertCategoryEnum } from "../misc/components/Alert";


declare const rootUrl: string;

export const ViewSubjectScreen = () => {
    const client = useWebPortalApiClient();
    const legacyClient = useWebPortalLegacyClient();
    const [dirty, setDirty] = React.useState(true); // indicates ui requires data initialization again
    const { study, dataItemConfigurationCollection, sessionTypes, populationGroups, studySubjectSetting } = useCurrentStudyConfigurationContext();
    const { subjectTypes, genders } = useReferenceContextProvider();
    const { id } = useParams<{ id: string }>();
    const [subject, setSubject] = React.useState<Subject>();
    const [notes, setNotes] = React.useState<SubjectNote[]>([]);
    const [notesVisible, setNotesVisible] = React.useState(false);
    const [revealPinModalVisible, setRevealPinModalVisible] = React.useState(false);
    const [toggleRandomModalVisible, setToggleRandomModalVisible] = React.useState(false);
    const [isAddingNewNote, setIsAddingNewNote] = React.useState(false);
    const [terminationDateVisible, setTerminationDateVisible] = React.useState(false);
    const { currentUserPermissions } = useSecurityContext();
    const [terminated, setTerminated] = React.useState<boolean>(false);
    const [subjectToUndoTermination, setSubjectToUndoTermination] = React.useState<Subject>();
    const [forceUpdateTable, setForceUpdateTable] = React.useState<boolean>(false);
    const [submittingUndoSubjectTermination, setSubmittingUndoSubjectTermination] = React.useState<boolean>(false);
    const [successAlert, setSuccessAlert] = React.useState<string>(null);
    const [undoSubjectTerminationButtonDisabled, setUndoSubjectTerminationButtonDisabled] = React.useState(false);
    const [warningAlert, setWarningAlert] = React.useState<string>(null);
    const [errorAlert, setErrorAlert] = React.useState<string>(null);
    const [terminationButtonDisabled, setTerminationButtonDisabled] = React.useState(false);
    const [auditModalVisible, setAuditModalVisible] = React.useState(false);
    const [notesError, setNotesError] = React.useState<string | undefined>();

    const handleTerminationButtonChange = (newValue: boolean) => {
        setTerminationButtonDisabled(newValue);
    }

    const handleAlertMessageChange = (alertMessage: string) => {
        if (alertMessage != undefined) {
            setSuccessAlert(alertMessage)
        }
    }

    const prepareUndoSubjectTermination = async (subjectId) => {
        const subject = await client.client.getSubject(subjectId);
        setSubjectToUndoTermination(subject);
        setAuditModalVisible(true);
    }

    const undoTerminateSubject = async (auditForm) => {
        setSubmittingUndoSubjectTermination(true);
        const editedSubject = await client.client.patchSubjects({
            terminationDate: null,
        } as SubjectMutation, subjectToUndoTermination.id, auditForm.Annotation);

        onTerminationAuditClose();
    }

    const onTerminationAuditClose = () => {
        setAuditModalVisible(false);
        setSubmittingUndoSubjectTermination(false);
        setSubjectToUndoTermination(null);
        setSuccessAlert("Subject has been updated successfully");
        setUndoSubjectTerminationButtonDisabled(true);
        setForceUpdateTable(!forceUpdateTable);
    }

    React.useEffect(() => {
        const getData = async () => {
            setSubject(null);
            const subject = await client.client.getSubject(id, undefined, "site($expand=study)");
            setSubject(subject);

            const notes = await client.client.getSubjectNotes(undefined, `subjectId eq ${id}`);
            setNotes(notes.value);
        }

        if (dirty)
            getData();

        setDirty(false);
    }, [dirty]);

    const isDisplayed = (item: ConfigurableDataItemIds) => {
        return dataItemConfigurationCollection.find(x => x.configurableDataItemId === item)?.importanceId !== DataItemImportance.NotApplicable;
    }

    const readOnlyField = (id: string, label: string, value: string | number) => (
        <div className="form-group col-md-4 col-sm-4 col-xs-12">
            <label htmlFor={id}>{label}</label>
            <input id={id} className="form-control" disabled readOnly value={value} data-automationid={`Field_${id}`} />
        </div>
    )

    const timeFieldNum = (id: string, label: string, value: string) => (
        <div className="form-group col-md-4 col-sm-4 col-xs-12">
            <label htmlFor={id}>{label}</label>
            <input type="time" className="form-control" disabled readOnly value={isoToHHmmss(value)} />
        </div>
    )

    const timeField = (id: string, label: string, value: string) => (
        <div className="form-group col-md-4 col-sm-4 col-xs-12">
            <label htmlFor={id}>{label}</label>
            <input type="time" className="form-control" disabled readOnly value={value} />
        </div>
    )

    const addNewNote = async (annotation: string) => {
        try {
            setIsAddingNewNote(true);
            setNotesError(undefined);
            await legacyClient.putSubjectNotes(id, annotation);
            const notes = await client.client.getSubjectNotes(undefined, `subjectId eq ${id}`);
            setNotes(notes.value);
        } catch (ex) {
            setNotesError(ex.Message);
        }

        setIsAddingNewNote(false);
    }

    const editNote = async (id: string, comment: string, annotation: string) => {
        try {
            setIsAddingNewNote(true);
            setNotesError(undefined);
            await client.client.patchSessionNote(
                {
                    comment: comment,
                }, id, annotation);
            const notes = await client.client.getSubjectNotes(undefined, `subjectId eq ${id}`);
            setNotes(notes.value);
        } catch (ex) {
            setNotesError(ex.Message);
        }

        setIsAddingNewNote(false);
    }
    const hasAddingNotePermission = currentUserPermissions.some(e => e.Id === PermissionEnum.AddSubjectNotes);
    const hasViewNotePermission = hasAddingNotePermission || currentUserPermissions.some(e => e.Id === PermissionEnum.ViewSubjectNotes);
    const hasEarlyTerminationPermission = currentUserPermissions.some(e => e.Id === PermissionEnum.EarlyTerminateSubject);

    if (!subject) return (<LoadingWithMessage message="Loading Subject..." />);

    return (
        <>
            <Alert show={successAlert != null} category={AlertCategoryEnum.Success}>
                {successAlert}
            </Alert>
            <Alert show={warningAlert !== null} category={AlertCategoryEnum.Warning}>
                {warningAlert}
            </Alert>
            <Alert show={errorAlert !== null} category={AlertCategoryEnum.Danger}>
                {errorAlert}
            </Alert>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">View Subject</h2>
                </div>
                <div className="col-md-6 h2">
                    <Link className="btn btn-primary btn-outline pull-right" to="/Subjects">
                        Return to List
                    </Link>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div id="demographicsPanel" className="panel panel-primary">
                        <div className="panel-heading">
                            <h4>Demographics</h4>
                        </div>
                        <div className="panel-body">
                            {readOnlyField("site", "Site", subject.site.number)}

                            {isDisplayed(ConfigurableDataItemIds.SubjectId) ?
                                readOnlyField("subject", "Subject ID", subject.number ? subject.number : "-")
                                : null}

                            {isDisplayed(ConfigurableDataItemIds.RandomizationNumber) ?
                                readOnlyField("randomizationNumber", "Randomization ID", subject.randomizationNumber ? subject.randomizationNumber : "-")
                                : null}

                            {isDisplayed(ConfigurableDataItemIds.SubjectGender) ?
                                readOnlyField("gender", "Gender", subject.gender ? genders.find(g => g.Id === subject.gender).Name : "-")
                                : null}

                            {isDisplayed(ConfigurableDataItemIds.SubjectDateOfBirth) ?
                                readOnlyField("dateOfBirth", "Date of Birth", subject.dateOfBirth ? Moment(subject.dateOfBirth).format("DD-MMM-YYYY") : "-")
                                : null}

                            {isDisplayed(ConfigurableDataItemIds.SubjectAge) ?
                                readOnlyField("age", "Age", subject.age ? subject.age : "-")
                                : null}

                            {isDisplayed(ConfigurableDataItemIds.SubjectPopulationGroup) ?
                                <>
                                    {readOnlyField("popGroup", "Population Group", subject.populationGroupId ? populationGroups.find(pg => pg.Id === subject.populationGroupId).Name : "-")}
                                    {readOnlyField("selfAssessedPopGroup", "Self Assessed Population Group", subject.race ? subject.race : "-")}
                                </>
                                : null}

                            {isDisplayed(ConfigurableDataItemIds.SubjectType) ?
                                readOnlyField("subjectType", "Subject Type", subject.status ? subjectTypes.find(st => st.id === subject.status).name : "-")
                                : null}

                            {isDisplayed(ConfigurableDataItemIds.SubjectSmokerType) ?
                                readOnlyField("smokingStatus", "Smoking Status", subject.smokerType ? subject.smokerType : "-")
                                : null}

                            {isDisplayed(ConfigurableDataItemIds.SubjectHeight) ?
                                readOnlyField("height", "Height", subject.height ? subject.height : "-")
                                : null}

                            {isDisplayed(ConfigurableDataItemIds.SubjectWeight) ?
                                readOnlyField("weight", "Weight", subject.weight ? subject.weight : "-")
                                : null}

                            {isDisplayed(ConfigurableDataItemIds.SubjectBmi) ?
                                readOnlyField("bmi", "BMI", subject.bmi ? subject.bmi : "-")
                                : null}
                        </div>
                    </div>
                    {study.sessionTypeIds.find(st => st === SessionTypes.EDiary) !== undefined ?
                        <div className="panel panel-primary">
                            <div className="panel-heading">
                                <h4>eDiary</h4>
                            </div>
                            <div className="panel-body">
                                {timeFieldNum("BedTime", "Bed Time", subject?.bedTime)}
                                {subject?.alarms.map(a => timeFieldNum(`Alarm${a.time}`, "Alarm", a.time))}

                                {isDisplayed(ConfigurableDataItemIds.SubjectLanguage) ?
                                    readOnlyField("language", "Language", subject.languageName ? subject.languageName : "-")
                                    : null}
                            </div>
                        </div> : null}
                </div>
            </div>
            <div className="panel panel-default">
                <div className="panel-body">
                    {
                        hasViewNotePermission ?
                            <button className="btn btn-primary btn-outline" onClick={() => setNotesVisible(true)} data-automationid="NotesButton">
                                Subject Notes
                            </button>
                            : null
                    }
                    &nbsp;
                    <Condition test={() => subject.pin !== null}>
                        <Authorization
                            permissionId={PermissionEnum.RevealAllSubjectsEDiaryPin}
                            studyId={subject.site.study.id}
                            currentPermissions={currentUserPermissions}
                        >
                            <button className="btn btn-primary btn-outline" onClick={() => setRevealPinModalVisible(true)} data-automationid="RevealPinButton">Reveal Subject PIN</button>
                        </Authorization>
                    </Condition>
                    &nbsp;
                    <Condition test={() => sessionTypes.find(st => st.Id === SessionTypes.Cough) !== undefined}>
                        <Authorization
                            permissionId={PermissionEnum.ViewHistoricalCoughSessionReport}
                            studyId={subject.site.study.id}
                            currentPermissions={currentUserPermissions}
                        >
                            <a className="btn btn-primary btn-outline" href={`${rootUrl}Reports/Preview/2?subjectId=${subject.id}`}>Historical Cough Session Report</a>
                        </Authorization>
                    </Condition>
                    &nbsp;
                    <Condition test={() => isDisplayed(ConfigurableDataItemIds.RandomizationNumber) || isDisplayed(ConfigurableDataItemIds.RandomizationDate)}>
                        <Authorization
                            permissionId={PermissionEnum.EditSubjectRandomization}
                            studyId={subject.site.study.id}
                            currentPermissions={currentUserPermissions}
                        >
                            <button className="btn btn-primary btn-outline" onClick={() => setToggleRandomModalVisible(true)} data-automationid="ToggleRandomizationButton">Randomize Subject</button>
                        </Authorization>
                    </Condition>
                    &nbsp;&nbsp;&nbsp;
                    {
                        studySubjectSetting?.canEarlyTerminateSubject && subject.terminationDate === null ?
                            <Authorization
                                permissionIdsAll={[PermissionEnum.EarlyTerminateSubject]}
                                studyId={subject.site.study.id}
                                currentPermissions={currentUserPermissions}
                            >

                                <button className="btn btn-primary btn-outline" disabled={terminationButtonDisabled} onClick={() => setTerminationDateVisible(true)}>Early Termination</button>
                            </Authorization> : subject.terminationDate !== null ?
                                <button disabled={undoSubjectTerminationButtonDisabled} className="btn btn-primary btn-outline" onClick={() => prepareUndoSubjectTermination(subject.id)}>Undo Early Termination</button> : null
                    }
                </div>
            </div>
            {
                !auditModalVisible ? null :
                    <AuditModal isSubmitting={submittingUndoSubjectTermination} onSubmit={(form) => undoTerminateSubject(form)} onClose={() => setAuditModalVisible(false)} header="Undo Terminate Subject" />
            }
            {
                hasEarlyTerminationPermission ?
                    <TerminationDateModal
                        subjectId={subject.id}
                        isVisible={terminationDateVisible}
                        header="Early Termination Date"
                        onCloseModal={() => setTerminationDateVisible(false)}
                        disabled={false}
                        onSubmit={{
                            earlyTerminationButtonEnabled: handleTerminationButtonChange,
                            alertMessage: handleAlertMessageChange,
                            dirty: setDirty
                        }}
                    />
                    : null
            }
            {
                hasViewNotePermission ?
                    <NotesModal
                        isVisible={notesVisible}
                        header="Subject Notes"
                        isAddingNote={isAddingNewNote}
                        notes={notes}
                        errorStatus={notesError}
                        onAdd={annotation => addNewNote(annotation)}
                        // onEdit={(id, comment, annotation) => editNote(id, comment, annotation)}
                        onCloseModal={() => setNotesVisible(false)}
                        disabled={!hasAddingNotePermission}
                    />
                    : null
            }
            {revealPinModalVisible ? <RevealPinModal subjectId={subject.id} onClose={() => setRevealPinModalVisible(false)} /> : null}
            {toggleRandomModalVisible ? <ToggleRandomizationModal subjectId={subject.id} onClose={() => {
                setToggleRandomModalVisible(false)
                setDirty(true)
            }} /> : null}
        </>
    );
}
